import React, { useEffect, useState } from 'react';
import {
  Button,
  Label,
  Modal,
  Select,
  TextInput,
  Textarea,
} from 'flowbite-react';
import { useTranslation } from 'react-i18next';
import { InterestGender } from '../../common/enums/InterestGender';
import { IIstiftaLink } from '../../common/interfaces/IstiftaLinks.interface';
import { useGetIstiftaLinkCategoriesQuery } from '../../redux/api/istiftaLinksApi';
import { useAppSelector } from '../../redux/hooks/useAppSelector';
import { selectIsDarkMode } from '../../redux/selectors/displaySettingsSelectors';

interface IstiftaLinkFormValues {
  descriptionAr: string;
  descriptionEn: string;
  url: string;
  categoryKey: string;
  interestGender: InterestGender;
}

interface IstiftaLinkModalProps {
  isOpen: boolean;
  initialValues?: IIstiftaLink;
  onClose: () => void;
  onSave: (values: IstiftaLinkFormValues) => void;
  isSaving: boolean;
}

const EMPTY_FORM: IstiftaLinkFormValues = {
  descriptionAr: '',
  descriptionEn: '',
  url: '',
  categoryKey: '',
  interestGender: InterestGender.BOTH,
};

function toFormValues(link: IIstiftaLink): IstiftaLinkFormValues {
  return {
    descriptionAr: link.description.ar,
    descriptionEn: link.description.en,
    url: link.url,
    categoryKey: link.category.key,
    interestGender: link.interestGender,
  };
}

function isValidUrl(value: string): boolean {
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
}

export const IstiftaLinkModal: React.FC<IstiftaLinkModalProps> = ({
  isOpen,
  initialValues,
  onClose,
  onSave,
  isSaving,
}) => {
  const { t } = useTranslation();
  const isDarkMode = useAppSelector(selectIsDarkMode);
  const { data: categories = [] } = useGetIstiftaLinkCategoriesQuery();

  const [form, setForm] = useState<IstiftaLinkFormValues>(EMPTY_FORM);
  const [errors, setErrors] = useState<
    Partial<Record<keyof IstiftaLinkFormValues, string>>
  >({});

  useEffect(() => {
    if (isOpen) {
      setForm(initialValues ? toFormValues(initialValues) : EMPTY_FORM);
      setErrors({});
    }
  }, [isOpen, initialValues]);

  const set = <K extends keyof IstiftaLinkFormValues>(
    key: K,
    value: IstiftaLinkFormValues[K]
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = (): boolean => {
    const next: typeof errors = {};
    if (!form.descriptionAr.trim())
      next.descriptionAr = t('istiftaLinks.form.required');
    if (!form.descriptionEn.trim())
      next.descriptionEn = t('istiftaLinks.form.required');
    if (!form.url.trim()) {
      next.url = t('istiftaLinks.form.required');
    } else if (!isValidUrl(form.url)) {
      next.url = t('istiftaLinks.form.urlInvalid');
    }
    if (!form.categoryKey) next.categoryKey = t('istiftaLinks.form.required');
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = () => {
    if (validate()) onSave(form);
  };

  const isEdit = Boolean(initialValues);

  return (
    <Modal show={isOpen} onClose={onClose} size="lg">
      <Modal.Header className="modal-header">
        {isEdit ? t('istiftaLinks.editLink') : t('istiftaLinks.createLink')}
      </Modal.Header>
      <Modal.Body>
        <div className="flex flex-col gap-4">
          <div>
            <Label
              htmlFor="descriptionAr"
              value={t('istiftaLinks.form.descriptionAr')}
            />
            <Textarea
              id="descriptionAr"
              dir="rtl"
              rows={3}
              value={form.descriptionAr}
              onChange={(e) => set('descriptionAr', e.target.value)}
              color={errors.descriptionAr ? 'failure' : undefined}
              helperText={errors.descriptionAr}
            />
          </div>
          <div>
            <Label
              htmlFor="descriptionEn"
              value={t('istiftaLinks.form.descriptionEn')}
            />
            <Textarea
              id="descriptionEn"
              dir="ltr"
              rows={3}
              value={form.descriptionEn}
              onChange={(e) => set('descriptionEn', e.target.value)}
              color={errors.descriptionEn ? 'failure' : undefined}
              helperText={errors.descriptionEn}
            />
          </div>
          <div>
            <Label htmlFor="url" value={t('istiftaLinks.form.url')} />
            <TextInput
              id="url"
              type="url"
              dir="ltr"
              value={form.url}
              onChange={(e) => set('url', e.target.value)}
              color={errors.url ? 'failure' : undefined}
              helperText={errors.url}
            />
          </div>
          <div>
            <Label
              htmlFor="categoryKey"
              value={t('istiftaLinks.form.category')}
            />
            <Select
              id="categoryKey"
              value={form.categoryKey}
              onChange={(e) => set('categoryKey', e.target.value)}
              color={errors.categoryKey ? 'failure' : undefined}
              helperText={errors.categoryKey}
            >
              <option value="" disabled />
              {categories.map((cat) => (
                <option key={cat.key} value={cat.key}>
                  {cat.name.ar}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <Label
              htmlFor="interestGender"
              value={t('istiftaLinks.form.gender')}
            />
            <Select
              id="interestGender"
              value={form.interestGender}
              onChange={(e) =>
                set('interestGender', e.target.value as InterestGender)
              }
            >
              {Object.values(InterestGender).map((g) => (
                <option key={g} value={g}>
                  {t(`istiftaLinks.gender.${g}`)}
                </option>
              ))}
            </Select>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          className="btn btn-dark"
          onClick={handleSave}
          disabled={isSaving}
          isProcessing={isSaving}
        >
          {t('istiftaLinks.form.save')}
        </Button>
        <Button
          className={`btn ${isDarkMode ? 'btn-dark' : 'btn-light'}`}
          onClick={onClose}
          disabled={isSaving}
        >
          {t('istiftaLinks.form.cancel')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
